import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, ItemsEditor, QuickVehicleDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, Search, UserPlus, Building2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useDuplicateGuard, DuplicateCustomerDialog } from "@/components/DuplicateCustomer";

const DOC_TYPES = [["ostra", "Faktúra"], ["ponuka", "Cenová ponuka"], ["zalohova", "Zálohová faktúra"]];
const VAT_CHOICES = [["SK", "SK – 23% DPH"], ["RC", "0% – do zahraničia"]];
const PAYMENT_TYPES = [["prevod", "Bankový prevod"], ["hotovost", "Hotovosť"], ["karta", "Platobná karta"], ["dobierka", "Dobierka"]];
const emptyCust = { name: "", ico: "", dic: "", ic_dph: "", address: "", city: "", psc: "", country: "SK", email: "" };
const iso = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 10); };
const empty = () => { const t = new Date(); const due = new Date(); due.setDate(due.getDate() + 7); return { doc_type: "ostra", customer_id: "", vehicle_id: "", vat_mode: "SK", items: [], issue_date: iso(t), due_date: iso(due), payment_type: "prevod", note: "", cust: { ...emptyCust } }; };

export default function InvoiceForm() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty());
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [savingCust, setSavingCust] = useState(false);
  const [ef, setEf] = useState({ status: "", mid: "", sent_at: "" });
  const [efSending, setEfSending] = useState(false);
  const [efTestOpen, setEfTestOpen] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const { dup, setDup, guard } = useDuplicateGuard();
  const setCust = (k, v) => setForm((f) => ({ ...f, cust: { ...f.cust, [k]: v } }));

  const saveCustomer = async () => {
    if (!form.cust.name.trim()) { toast.error("Zadajte názov / meno"); return; }
    setSavingCust(true);
    try { await guard(form.cust.name, "", doSaveCustomer); } finally { setSavingCust(false); }
  };
  const doSaveCustomer = async () => {
    setSavingCust(true);
    try {
      const { data } = await api.post("/customers", { ...form.cust });
      setCustomers((await api.get("/customers")).data);
      setForm((f) => ({ ...f, customer_id: data.id }));
      toast.success("Zákazník uložený a priradený");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingCust(false); }
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/vehicles").then((r) => setVehicles(r.data));
  }, []);
  useEffect(() => {
    if (id) {
      api.get(`/invoices/${id}`).then((r) => {
        const inv = r.data;
        setForm({ doc_type: inv.doc_type, vat_mode: inv.vat_mode === "RC" ? "RC" : "SK", customer_id: inv.customer_id || "", person_id: inv.person_id || "", vehicle_id: inv.vehicle_id || "",
          items: (inv.items || []).map((it) => ({ ...it })), issue_date: inv.issue_date || iso(new Date()), due_date: inv.due_date || "", payment_type: inv.payment_type || "prevod", note: inv.note || "", cust: { ...emptyCust, ...(inv.customer || {}) } });
        setEf({ status: inv.efaktura_status || "", mid: inv.efaktura_message_id || "", sent_at: inv.efaktura_sent_at || "" });
        setLoading(false);
      });
    } else if (location.state?.fromJob) {
      const job = location.state.fromJob;
      setForm((f) => ({ ...f, doc_type: location.state.docType || f.doc_type, customer_id: job.customer_id || "", person_id: job.customer_id || "", vehicle_id: job.vehicle_id || "", job_id: job.id, vat_mode: job.vat_mode === "RC" ? "RC" : "SK", items: (job.items || []).map((it) => ({ ...it })), note: job.title }));
    }
  }, [id, location.state]);

  const setVatMode = (mode) => setForm((f) => ({ ...f, vat_mode: mode, items: f.items.map((it) => ({ ...it, vat_rate: mode === "RC" ? 0 : 23 })) }));

  useEffect(() => {
    if (form.customer_id && !form.cust.name && customers.length) {
      const c = customers.find((x) => x.id === form.customer_id);
      if (c) setForm((f) => ({ ...f, cust: { name: c.name || "", ico: c.ico || "", dic: c.dic || "", ic_dph: c.ic_dph || "", address: c.address || "", city: c.city || "", psc: c.psc || "", country: c.country || "SK", email: c.email || "" } }));
    }
  }, [customers, form.customer_id, form.cust.name]);

  const custOf = (c) => ({ name: c.name || "", ico: c.ico || "", dic: c.dic || "", ic_dph: c.ic_dph || "", address: c.address || "", city: c.city || "", psc: c.psc || "", country: c.country || "SK", email: c.email || "" });
  const onCustomer = (v) => {
    if (v === "none") { setForm((f) => ({ ...f, customer_id: "", person_id: "", vehicle_id: "" })); return; }
    const c = customers.find((x) => x.id === v) || {};
    setForm((f) => ({ ...f, customer_id: v, person_id: c.is_company === false ? v : "", vehicle_id: "", cust: custOf(c) }));
  };
  const [links, setLinks] = useState([]);
  const preselected = useRef(false);
  useEffect(() => {
    if (!form.person_id) { setLinks([]); return; }
    api.get(`/customers/${form.person_id}/companies`).then((r) => setLinks(r.data || [])).catch(() => setLinks([]));
  }, [form.person_id]);
  const billTo = (cid) => {
    const c = links.find((x) => x.id === cid) || customers.find((x) => x.id === cid) || {};
    setForm((f) => ({ ...f, customer_id: cid, cust: custOf(c) }));
  };
  useEffect(() => {
    if (id || preselected.current || !links.length || form.customer_id !== form.person_id) return;
    const v = vehicles.find((x) => x.id === form.vehicle_id);
    if (v?.owner_company_id && links.some((l) => l.id === v.owner_company_id)) { preselected.current = true; billTo(v.owner_company_id); }
  }, [links, vehicles, form.vehicle_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const ownerId = form.person_id || form.customer_id;

  const lookup = async () => {
    setLookupLoading(true);
    try {
      const { data } = await api.get("/lookup/company", { params: { country: form.cust.country, q: form.cust.ico || form.cust.name } });
      if (data.found) {
        setForm((f) => ({ ...f, cust: { ...f.cust, name: data.name || f.cust.name, ico: data.ico || f.cust.ico, dic: data.dic || f.cust.dic, ic_dph: data.ic_dph || f.cust.ic_dph, address: data.address || f.cust.address, city: data.city || f.cust.city, psc: data.psc || f.cust.psc, country: data.country || f.cust.country } }));
        toast.success("Údaje firmy doplnené");
      } else toast.warning(data.message || "Firma sa nenašla – doplňte ručne");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLookupLoading(false); }
  };

  const searchTimer = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [vehOpen, setVehOpen] = useState(false);

  const onNameChange = (v) => {
    setForm((f) => ({ ...f, cust: { ...f.cust, name: v } }));
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (v.trim().length < 3) { setSuggestions([]); setSuggestOpen(false); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true); setSuggestOpen(true);
      try {
        const { data } = await api.get("/lookup/company/search", { params: { country: form.cust.country, q: v.trim() } });
        setSuggestions(data.results || []);
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 400);
  };

  const pickCompany = async (c) => {
    setSuggestOpen(false); setSuggestions([]);
    setForm((f) => ({ ...f, cust: { ...f.cust, name: c.name || "", ico: c.ico || "", address: c.address || f.cust.address, city: c.city || f.cust.city, psc: c.psc || f.cust.psc, country: c.country || f.cust.country } }));
    if (!c.ico) return;
    setLookupLoading(true);
    try {
      const { data } = await api.get("/lookup/company", { params: { country: c.country || form.cust.country, q: c.ico } });
      if (data.found) {
        setForm((f) => ({ ...f, cust: { ...f.cust, name: data.name || c.name, ico: data.ico || c.ico, dic: data.dic || f.cust.dic, ic_dph: data.ic_dph || f.cust.ic_dph, address: data.address || f.cust.address, city: data.city || f.cust.city, psc: data.psc || f.cust.psc, country: data.country || f.cust.country } }));
      }
      toast.success("Údaje firmy doplnené");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLookupLoading(false); }
  };

  const buildSnapshots = () => {
    const v = vehicles.find((x) => x.id === form.vehicle_id);
    const c = form.cust || {};
    return {
      customer: { name: c.name || "", ico: c.ico || "", dic: c.dic || "", ic_dph: c.ic_dph || "", address: c.address || "", city: c.city || "", psc: c.psc || "", country: c.country || "SK", email: c.email || "" },
      vehicle: v ? { spz: v.spz, vin: v.vin, brand: v.brand, model: v.model, year: v.year } : {},
    };
  };

  const downloadUbl = async () => {
    try {
      const res = await api.get(`/invoices/${id}/ubl`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `efaktura_${id}.xml`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("eFaktúru sa nepodarilo vygenerovať"); }
  };

  const sendEfaktura = async () => {
    if (!isAdmin) { setEfTestOpen(true); return; }
    setEfSending(true);
    try {
      const { data } = await api.post(`/invoices/${id}/efaktura/send`);
      setEf({ status: data.status, mid: data.message_id });
      toast.success(data.mode === "mock" ? "Odoslané cez Peppol (testovací režim)" : "Faktúra odoslaná cez Peppol");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setEfSending(false); }
  };

  const EF_LABEL = { delivered: "Doručené", sent: "Odoslané", failed: "Zlyhalo", queued: "V rade" };

  const save = async () => {
    if (!form.items.length) { toast.error("Pridajte aspoň jednu položku"); return; }    setSaving(true);
    const { customer, vehicle } = buildSnapshots();
    const payload = {
      doc_type: form.doc_type, vat_mode: form.vat_mode, customer, customer_id: form.customer_id || null, person_id: customers.find((c) => c.id === form.person_id)?.is_company === false ? form.person_id : null,
      vehicle, vehicle_id: form.vehicle_id || null, job_id: form.job_id || null,
      issue_date: form.issue_date || "", due_date: form.due_date || "", payment_type: form.payment_type || "prevod", note: form.note || "",
      items: form.items.map((it) => ({ description: it.description, kind: it.kind || "praca", qty: Number(it.qty), unit: it.unit || "", unit_price: Number(it.unit_price || 0), discount: Number(it.discount || 0), vat_rate: form.vat_mode === "RC" ? 0 : Number(it.vat_rate || 0) })),
    };
    try {
      if (id) { const { data } = await api.put(`/invoices/${id}`, payload); toast.success(`Uložené č. ${data.number}`); }
      else { const { data } = await api.post("/invoices", payload); toast.success(`Vytvorené č. ${data.number}`); }
      navigate("/faktury");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <Button variant="ghost" className="mb-4 gap-2" onClick={() => navigate("/faktury")} data-testid="invoice-form-back"><ArrowLeft className="h-4 w-4" /> Späť</Button>
      <PageHeader title={id ? "Upraviť faktúru" : "Nová faktúra"} subtitle="Vyplňte údaje dokladu" />
      <div className="mx-auto max-w-5xl space-y-4">
        <div className="card-surface p-4 space-y-4">
          <div>
            <Label className="mb-2 block">Sadzba DPH pre položky</Label>
            <div className="flex gap-1 rounded-lg border border-border bg-background/60 p-1">
              {VAT_CHOICES.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setVatMode(k)} data-testid={`invoice-vat-${k}`} className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${form.vat_mode === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Typ dokladu</Label>
              <Select value={form.doc_type} onValueChange={(v) => set("doc_type", v)}><SelectTrigger data-testid="invoice-type-select"><SelectValue /></SelectTrigger><SelectContent>{DOC_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Dátum vystavenia</Label><Input type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} data-testid="invoice-issue-input" /></div>
            <div className="space-y-1.5"><Label>Splatnosť</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-input" /></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>Forma úhrady</Label>
              <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}><SelectTrigger data-testid="invoice-payment-select"><SelectValue /></SelectTrigger><SelectContent>{PAYMENT_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label>Zákazník (voliteľné)</Label>
              <Select value={form.person_id || form.customer_id || "none"} onValueChange={onCustomer}><SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder="Vyberte alebo vyplňte nižšie" /></SelectTrigger><SelectContent><SelectItem value="none">Bez uloženého zákazníka</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Vozidlo</Label><button type="button" onClick={() => setVehOpen(true)} className="text-xs font-semibold text-primary hover:underline" data-testid="invoice-quick-add-vehicle">+ Nové vozidlo</button></div>
              <Select value={form.vehicle_id || "none"} onValueChange={(v) => set("vehicle_id", v === "none" ? "" : v)}><SelectTrigger data-testid="invoice-vehicle-select"><SelectValue placeholder="Bez vozidla" /></SelectTrigger><SelectContent><SelectItem value="none">Bez vozidla</SelectItem>{(ownerId ? vehicles.filter((v) => v.customer_id === ownerId) : vehicles).map((v) => <SelectItem key={v.id} value={v.id}>{v.spz} · {v.brand} {v.model}</SelectItem>)}</SelectContent></Select>
              {ownerId && !vehicles.some((v) => v.customer_id === ownerId) && <p className="text-xs text-muted-foreground">Tento zákazník nemá priradené vozidlo. Pridajte ho cez „+ Nové vozidlo".</p>}
            </div>
          </div>
        </div>
        <div className="card-surface relative z-30 p-5 space-y-4">
          {form.person_id && links.length > 0 && (
            <div className="space-y-1.5" data-testid="invoice-bill-to">
              <Label className="text-base font-bold">Fakturovať na</Label>
              <div className="flex flex-wrap gap-2">
                {[customers.find((c) => c.id === form.person_id) || { id: form.person_id, name: "Osoba" }, ...links].map((c, i) => (
                  <button key={c.id} type="button" onClick={() => billTo(c.id)} data-testid={i === 0 ? "invoice-bill-to-person" : `invoice-bill-to-${c.id}`}
                    className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${form.customer_id === c.id ? "border-primary bg-primary/15 text-foreground" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                    <div className="font-semibold">{c.name}</div><div className="text-[11px]">{i === 0 ? "osoba" : `firma${c.ico ? ` · IČO ${c.ico}` : ""}`}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <Label className="text-base font-bold">Fakturačné údaje odberateľa</Label>
            <div className="flex items-center gap-1 rounded-lg border border-border bg-background/60 p-1">
              {["SK", "CZ"].map((cc) => (<button key={cc} type="button" onClick={() => setCust("country", cc)} data-testid={`inv-cust-country-${cc}`} className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${form.cust.country === cc ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{cc}</button>))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 relative"><Label>Názov / meno</Label>
              <Input value={form.cust.name} onChange={(e) => onNameChange(e.target.value)} onFocus={() => { if (suggestions.length) setSuggestOpen(true); }} onBlur={() => setTimeout(() => setSuggestOpen(false), 150)} autoComplete="off" placeholder="Začnite písať názov firmy…" data-testid="inv-cust-name" />
              {suggestOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden" data-testid="inv-cust-suggestions">
                  {searching && <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Hľadám…</div>}
                  {!searching && suggestions.length === 0 && <div className="px-3 py-2.5 text-sm text-muted-foreground">Žiadne firmy – doplňte ručne</div>}
                  {!searching && suggestions.map((c, i) => (
                    <button key={`${c.ico}-${i}`} type="button" onMouseDown={(e) => { e.preventDefault(); pickCompany(c); }} data-testid={`inv-cust-suggestion-${i}`} className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent transition-colors border-b border-border/50 last:border-0">
                      <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                      <span className="min-w-0"><span className="block font-medium truncate">{c.name}</span><span className="block text-xs text-muted-foreground">IČO {c.ico}{c.city ? ` · ${c.city}` : ""}</span></span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>IČO</Label><div className="flex gap-2"><Input value={form.cust.ico} onChange={(e) => setCust("ico", e.target.value)} data-testid="inv-cust-ico" /><Button type="button" variant="outline" onClick={lookup} disabled={lookupLoading} data-testid="inv-cust-lookup" className="whitespace-nowrap gap-1">{lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Nájsť firmu</Button></div></div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5">DIČ {lookupLoading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}</Label><Input value={form.cust.dic} onChange={(e) => setCust("dic", e.target.value)} placeholder={lookupLoading ? "Načítavam…" : ""} data-testid="inv-cust-dic" /></div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5">IČ DPH {lookupLoading && <Loader2 className="h-3 w-3 animate-spin text-primary" />}</Label><Input value={form.cust.ic_dph} onChange={(e) => setCust("ic_dph", e.target.value)} placeholder={lookupLoading ? "Načítavam…" : ""} data-testid="inv-cust-icdph" /></div>
          </div>
          <div className="space-y-1.5"><Label>Adresa</Label><Input value={form.cust.address} onChange={(e) => setCust("address", e.target.value)} data-testid="inv-cust-address" /></div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label>PSČ</Label><Input value={form.cust.psc} onChange={(e) => setCust("psc", e.target.value)} data-testid="inv-cust-psc" /></div>
            <div className="space-y-1.5"><Label>Mesto</Label><Input value={form.cust.city} onChange={(e) => setCust("city", e.target.value)} data-testid="inv-cust-city" /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.cust.email} onChange={(e) => setCust("email", e.target.value)} data-testid="inv-cust-email" /></div>
          </div>
          <div className="flex justify-end">
            <Button type="button" variant="secondary" className="gap-2" onClick={saveCustomer} disabled={savingCust || !!form.customer_id} data-testid="inv-save-customer">{savingCust ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}{form.customer_id ? "Zákazník uložený" : "Uložiť ako zákazníka"}</Button>
          </div>
        </div>
        <div className="card-surface p-5">
          <Label className="mb-3 block text-base font-bold">Položky</Label>
          <ItemsEditor items={form.items} onChange={(v) => set("items", v)} vatMode={form.vat_mode} />
        </div>
        <div className="card-surface p-5 space-y-1.5"><Label>Poznámka</Label><Textarea value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="invoice-note-input" /></div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {id && ef.status && (
            <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${ef.status === "delivered" ? "bg-emerald-500/15 text-emerald-600" : ef.status === "failed" ? "bg-red-500/15 text-red-600" : "bg-sky-500/15 text-sky-600"}`} data-testid="efaktura-status-badge">
              Peppol: {EF_LABEL[ef.status] || ef.status}{ef.sent_at ? ` · ${new Date(ef.sent_at).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
            </span>
          )}
          <Button variant="ghost" onClick={() => navigate("/faktury")}>Zrušiť</Button>
          {id && <Button type="button" variant="outline" onClick={downloadUbl} data-testid="download-ubl-button">eFaktúra (XML)</Button>}
          {id && form.doc_type === "ostra" && <Button type="button" variant="secondary" onClick={sendEfaktura} disabled={efSending} data-testid="send-efaktura-button">{efSending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Odoslať cez Peppol"}</Button>}
          <Button onClick={save} disabled={saving} data-testid="save-invoice-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (id ? "Uložiť zmeny" : "Vytvoriť faktúru")}</Button>
        </div>
      </div>

      <QuickVehicleDialog open={vehOpen} onOpenChange={setVehOpen} customerId={form.customer_id}
        onCreated={(v) => { setVehicles((prev) => [...prev, v]); set("vehicle_id", v.id); }} />
      <DuplicateCustomerDialog dup={dup} setDup={setDup} openLabel="Použiť existujúceho" onOpenExisting={(c) => { onCustomer(c.id); toast.success("Priradený existujúci zákazník"); }} />

      <Dialog open={efTestOpen} onOpenChange={setEfTestOpen}>
        <DialogContent data-testid="efaktura-test-mode-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading uppercase"><Clock className="h-5 w-5 text-primary" /> Čoskoro dostupné</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Odosielanie faktúr cez Peppol (eFaktúra) je zatiaľ v testovacom režime. Čoskoro bude dostupné pre všetky účty. Ďakujeme za trpezlivosť.</p>
          <DialogFooter>
            <Button onClick={() => setEfTestOpen(false)} data-testid="efaktura-test-mode-close">Rozumiem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
